/**
 * Controlled Feel / Style / Flavor vocabulary.
 *
 * IDs are the stable identity. Display copy is never stored as identity.
 * Colors are an initial visual choice and must not be the only selection cue.
 *
 * Flavor groups are presentational. Stored flavorIds stay one unordered set.
 * No parent-genre map and no custom categories in this phase.
 */

export const VIBE_SCHEMA_VERSION = 1;

export const MAX_STYLES = 5;

/** Rules bound. Above the current 20 so a later vocabulary bump is not a rules migration. */
export const MAX_FLAVORS = 32;

export const FEEL_COLORS = {
  cyan: '#22D3EE',
  gold: '#FFE14A',
  lavender: '#A78BFA',
  indigo: '#7C5CFF',
  coral: '#FF6B4A',
};

export const FEELS = [
  {
    id: 'hype_electric',
    label: 'Hype & Electric',
    lines: ['Hype &', 'Electric'],
    color: FEEL_COLORS.cyan,
    colorName: 'bright cyan / aqua',
    symbol: '⚡',
  },
  {
    id: 'feel_good_bright',
    label: 'Feel-Good & Bright',
    lines: ['Feel-Good &', 'Bright'],
    color: FEEL_COLORS.gold,
    colorName: 'luminous golden yellow',
    symbol: '✨',
  },
  {
    id: 'chill_easygoing',
    label: 'Chill & Easygoing',
    lines: ['Chill &', 'Easygoing'],
    color: FEEL_COLORS.lavender,
    colorName: 'light lavender / purple',
    symbol: '🌊',
  },
  {
    id: 'deep_reflective',
    label: 'Deep & Reflective',
    lines: ['Deep &', 'Reflective'],
    color: FEEL_COLORS.indigo,
    colorName: 'rich saturated indigo / violet',
    symbol: '◇',
  },
  {
    id: 'warm_nostalgic',
    label: 'Warm & Nostalgic',
    lines: ['Warm &', 'Nostalgic'],
    color: FEEL_COLORS.coral,
    colorName: 'glowing coral / warm orange',
    symbol: '♥',
  },
];

export const STYLES = [
  { id: 'pop', label: 'Pop' },
  { id: 'hip_hop_rap', label: 'Hip-Hop / Rap' },
  { id: 'rnb', label: 'R&B' },
  { id: 'country_texas_country', label: 'Country / Texas Country' },
  { id: 'americana', label: 'Americana' },
  { id: 'folk', label: 'Folk' },
  { id: 'indie_rock', label: 'Indie Rock' },
  { id: 'alternative_rock', label: 'Alternative Rock' },
  { id: 'classic_rock', label: 'Classic Rock' },
  { id: 'emo', label: 'Emo' },
  { id: 'pop_punk', label: 'Pop Punk' },
  { id: 'metal', label: 'Metal' },
  { id: 'house', label: 'House' },
  { id: 'edm', label: 'EDM' },
  { id: 'disco', label: 'Disco' },
  { id: 'jazz', label: 'Jazz' },
  { id: 'blues', label: 'Blues' },
  { id: 'funk', label: 'Funk' },
  { id: 'latin_pop', label: 'Latin Pop' },
  { id: 'reggaeton', label: 'Reggaeton' },
  { id: 'regional_mexican', label: 'Regional Mexican' },
  { id: 'afrobeats', label: 'Afrobeats' },
  { id: 'reggae', label: 'Reggae' },
  { id: 'bollywood', label: 'Bollywood' },
];

/** Definition order is the tie-break order. It is not a ranking. */
export const FLAVORS = [
  { id: '80s', label: '80s' },
  { id: '90s', label: '90s' },
  { id: '2000s', label: '2000s' },
  { id: '2010s', label: '2010s' },
  { id: 'current', label: 'Current' },
  { id: 'unplugged', label: 'Unplugged' },
  { id: 'raw_intimate', label: 'Raw & Intimate' },
  { id: 'big_bold', label: 'Big & Bold' },
  { id: 'club_ready', label: 'Club Ready' },
  { id: 'smooth_groovy', label: 'Smooth & Groovy' },
  { id: 'layered_atmospheric', label: 'Layered & Atmospheric' },
  { id: 'singalong', label: 'Singalong' },
  { id: 'live_energy', label: 'Live Energy' },
  { id: 'remixed', label: 'Remixed' },
  { id: 'sleek_polished', label: 'Sleek & Polished' },
  { id: 'bass_heavy', label: 'Bass-Heavy' },
  { id: 'guitar_driven', label: 'Guitar-Driven' },
  { id: 'minimal_stripped', label: 'Minimal & Stripped' },
  { id: 'slow_burn', label: 'Slow Burn' },
  { id: 'experimental', label: 'Experimental' },
];

export const FLAVOR_GROUPS = [
  {
    id: 'era',
    label: 'Era',
    flavorIds: ['80s', '90s', '2000s', '2010s', 'current'],
  },
  {
    id: 'character',
    label: 'Character',
    flavorIds: [
      'raw_intimate',
      'big_bold',
      'smooth_groovy',
      'layered_atmospheric',
      'sleek_polished',
      'minimal_stripped',
      'slow_burn',
      'experimental',
    ],
  },
  {
    id: 'performance',
    label: 'Performance / Production',
    flavorIds: [
      'unplugged',
      'club_ready',
      'singalong',
      'live_energy',
      'remixed',
      'bass_heavy',
      'guitar_driven',
    ],
  },
];

export const FEEL_BY_ID = Object.fromEntries(FEELS.map((feel) => [feel.id, feel]));
export const STYLE_BY_ID = Object.fromEntries(STYLES.map((style) => [style.id, style]));
export const FLAVOR_BY_ID = Object.fromEntries(FLAVORS.map((flavor) => [flavor.id, flavor]));

export const FEEL_IDS = FEELS.map((feel) => feel.id);
export const STYLE_IDS = STYLES.map((style) => style.id);
export const FLAVOR_IDS = FLAVORS.map((flavor) => flavor.id);

const STYLE_LIMIT_MESSAGE = '5 styles selected — remove one to add another.';

export function styleLimitMessage() {
  return STYLE_LIMIT_MESSAGE;
}

export function definitionIndex(ids, id) {
  const index = ids.indexOf(id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}
